'use strict';

/**
 * Touch zoom for the product images.
 *
 * Zooms the image itself (a CSS transform on the `img`) instead of the whole page:
 *   - pinch with two fingers to zoom in and out, anchored on the point between the fingers;
 *   - double tap to toggle between the normal size and `DOUBLE_TAP_SCALE`, anchored on the tap;
 *   - drag with one finger to pan while the image is zoomed in.
 *
 * As long as the image sits at its normal size nothing is intercepted: one finger scrolls the
 * page and swipes the carousel exactly as before. Only once a second finger lands, or once the
 * image is zoomed in, does this module take over the gesture.
 *
 * Built on touch events (available on every mobile browser, unlike pointer events on older iOS)
 * with `touch-action` telling the browser to stay out of the way. Only wired up for touch
 * sessions, the desktop hover zoom (jquery-zoom) is never active at the same time.
 */

var MAX_SCALE = 3;
var DOUBLE_TAP_SCALE = 2.5;

// A second tap counts as a double tap when it lands quickly and close enough to the first one.
var DOUBLE_TAP_DELAY = 300;
var DOUBLE_TAP_DISTANCE = 40;

// Movement (px) above which a touch is a drag rather than a tap.
var TAP_MOVE_TOLERANCE = 10;

// Scales this close to 1 are treated as "not zoomed", floating point never lands exactly on 1.
var SCALE_EPSILON = 0.01;

var ANIMATION_MS = 250;

var ENABLED_CLASS = 'js-touch-zoom';
var ZOOMED_CLASS = 'is-zoomed';
var ROOT_SELECTOR = '.primary-images .carousel, .primary-images .preloaded-image';

// Taps on the carousel chrome must not zoom the image, the indicators contain thumbnails.
var CHROME_SELECTOR = '.carousel-indicators, .carousel-control-prev, .carousel-control-next';

var controllers = new WeakMap();

/**
 * Distance between two points.
 * @param {number} ax - first point x
 * @param {number} ay - first point y
 * @param {number} bx - second point x
 * @param {number} by - second point y
 * @returns {number} distance in pixels
 */
function distance(ax, ay, bx, by) {
    var dx = ax - bx;
    var dy = ay - by;

    return Math.sqrt((dx * dx) + (dy * dy));
}

/**
 * Keeps a value inside a range.
 * @param {number} value - value to clamp
 * @param {number} min - lower bound
 * @param {number} max - upper bound
 * @returns {number} the clamped value
 */
function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

/**
 * Builds the gesture controller for one image container.
 * @param {Element} root - the carousel (or single image wrapper) holding the product image
 * @returns {Object} the controller
 */
function createController(root) {
    var img = null;
    var scale = 1;
    var tx = 0;
    var ty = 0;

    // Centre of the image as it would sit without any transform, in viewport coordinates.
    var origin = { x: 0, y: 0 };

    var enabled = false;

    // Snapshot of the transform when the current pinch started.
    var pinch = null;
    // Snapshot of the transform and finger position when the current pan started.
    var pan = null;

    var touchStart = null;
    var touchMoved = false;
    var lastTapTime = 0;
    var lastTapPoint = null;

    // Bootstrap drives the carousel swipe from pointer events, which touch handlers cannot stop,
    // so they are suppressed separately while a gesture belongs to us.
    var pointersDown = 0;
    var suppressCarousel = false;

    /**
     * @returns {boolean} true when the image is currently scaled up
     */
    function isZoomed() {
        return scale > 1 + SCALE_EPSILON;
    }

    /**
     * Looks up the image the gestures apply to, the `.zoomImg` overlay of the desktop hover zoom
     * is skipped so the two never fight over the same element.
     * @returns {Element|null} the visible product image
     */
    function activeImage() {
        return root.querySelector('.carousel-item.active img:not(.zoomImg)')
            || root.querySelector('img:not(.zoomImg)');
    }

    /**
     * Writes the current transform to the image.
     * @param {boolean} animate - true to ease into the new transform
     */
    function render(animate) {
        root.classList.toggle(ZOOMED_CLASS, isZoomed());

        if (!img) {
            return;
        }

        img.style.transition = animate ? 'transform ' + ANIMATION_MS + 'ms ease-out' : 'none';
        img.style.transform = 'translate3d(' + tx + 'px, ' + ty + 'px, 0) scale(' + scale + ')';
    }

    /**
     * Keeps the scaled image covering its frame, so panning can never expose an empty edge.
     */
    function clampTranslate() {
        if (!img) {
            return;
        }

        var maxX = Math.max(0, ((img.offsetWidth * scale) - img.offsetWidth) / 2);
        var maxY = Math.max(0, ((img.offsetHeight * scale) - img.offsetHeight) / 2);

        tx = clamp(tx, -maxX, maxX);
        ty = clamp(ty, -maxY, maxY);
    }

    /**
     * Returns the image to its normal size.
     * @param {boolean} animate - true to ease back
     */
    function reset(animate) {
        scale = 1;
        tx = 0;
        ty = 0;
        pinch = null;
        pan = null;
        lastTapTime = 0;
        lastTapPoint = null;
        render(animate);
    }

    /**
     * Measures the untransformed centre of the image. Scaling happens around that centre, so it
     * stays put and can be recovered from the current bounding box minus the translation.
     */
    function measureOrigin() {
        var rect = img.getBoundingClientRect();

        origin.x = (rect.left + (rect.width / 2)) - tx;
        origin.y = (rect.top + (rect.height / 2)) - ty;
    }

    /**
     * Moves the image so that the point which sat under `fromPoint` at the reference transform
     * sits under `toPoint` at the new scale, which is what makes a pinch feel anchored.
     * @param {number} nextScale - the scale to apply
     * @param {Object} reference - transform the gesture started from ({scale, tx, ty})
     * @param {Object} fromPoint - viewport point the gesture started at
     * @param {Object} toPoint - viewport point the gesture is at now
     */
    function zoomAround(nextScale, reference, fromPoint, toPoint) {
        var ratio = nextScale / reference.scale;

        scale = nextScale;
        tx = toPoint.x - origin.x - ((fromPoint.x - origin.x - reference.tx) * ratio);
        ty = toPoint.y - origin.y - ((fromPoint.y - origin.y - reference.ty) * ratio);
        clampTranslate();
    }

    /**
     * Toggles the double tap zoom, anchored on the tapped point.
     * @param {Object} point - viewport coordinates of the tap
     */
    function toggleDoubleTapZoom(point) {
        img = activeImage();

        if (!img) {
            return;
        }

        if (isZoomed()) {
            reset(true);
            return;
        }

        measureOrigin();
        zoomAround(DOUBLE_TAP_SCALE, { scale: scale, tx: tx, ty: ty }, point, point);
        render(true);
    }

    /**
     * Decides whether the tap that just ended completes a double tap.
     * @param {TouchEvent} event - the touchend event
     */
    function handleTap(event) {
        var now = Date.now();
        var point = touchStart;
        var isDoubleTap = lastTapPoint
            && (now - lastTapTime) < DOUBLE_TAP_DELAY
            && distance(point.x, point.y, lastTapPoint.x, lastTapPoint.y) < DOUBLE_TAP_DISTANCE;

        if (!isDoubleTap) {
            lastTapTime = now;
            lastTapPoint = point;
            return;
        }

        lastTapTime = 0;
        lastTapPoint = null;
        suppressCarousel = true;

        if (event.cancelable) {
            event.preventDefault();
        }
        event.stopPropagation();

        toggleDoubleTapZoom(point);
    }

    /**
     * @param {TouchEvent} event - a touch event
     * @returns {boolean} true when the touch started on the product image, not on the carousel
     *     arrows or on a thumbnail
     */
    function isImageTouch(event) {
        var target = event.target;

        if (!target || !target.closest || target.closest(CHROME_SELECTOR)) {
            return false;
        }

        return !!target.closest('.carousel-item') || root.classList.contains('preloaded-image');
    }

    /**
     * @param {TouchEvent} event - the touchstart event
     */
    function onTouchStart(event) {
        if (!enabled || !isImageTouch(event)) {
            return;
        }

        var touches = event.touches;

        img = activeImage();

        if (!img) {
            return;
        }

        if (touches.length === 2) {
            // A pinch is ours from the first moment, preventing the default here stops the
            // browser from starting its own pan or page zoom for the rest of the gesture.
            suppressCarousel = true;
            touchMoved = true;
            pan = null;
            measureOrigin();
            pinch = {
                scale: scale,
                tx: tx,
                ty: ty,
                distance: distance(
                    touches[0].clientX, touches[0].clientY,
                    touches[1].clientX, touches[1].clientY
                ),
                mid: {
                    x: (touches[0].clientX + touches[1].clientX) / 2,
                    y: (touches[0].clientY + touches[1].clientY) / 2
                }
            };

            if (event.cancelable) {
                event.preventDefault();
            }
            event.stopPropagation();
        } else if (touches.length === 1) {
            touchStart = { x: touches[0].clientX, y: touches[0].clientY };
            touchMoved = false;

            if (isZoomed()) {
                suppressCarousel = true;
                pan = { x: touchStart.x, y: touchStart.y, tx: tx, ty: ty };
                event.stopPropagation();
            } else {
                // Not zoomed: leave the gesture alone so the page scrolls and the carousel swipes.
                pan = null;
            }
        }
    }

    /**
     * @param {TouchEvent} event - the touchmove event
     */
    function onTouchMove(event) {
        if (!enabled || !img) {
            return;
        }

        var touches = event.touches;

        if (touches.length === 2 && pinch && pinch.distance) {
            var current = distance(
                touches[0].clientX, touches[0].clientY,
                touches[1].clientX, touches[1].clientY
            );
            var mid = {
                x: (touches[0].clientX + touches[1].clientX) / 2,
                y: (touches[0].clientY + touches[1].clientY) / 2
            };

            zoomAround(
                clamp(pinch.scale * (current / pinch.distance), 1, MAX_SCALE),
                pinch,
                pinch.mid,
                mid
            );
            render(false);

            if (event.cancelable) {
                event.preventDefault();
            }
            event.stopPropagation();
        } else if (touches.length === 1) {
            if (touchStart
                && distance(touches[0].clientX, touches[0].clientY, touchStart.x, touchStart.y) > TAP_MOVE_TOLERANCE) {
                touchMoved = true;
            }

            if (pan) {
                tx = pan.tx + (touches[0].clientX - pan.x);
                ty = pan.ty + (touches[0].clientY - pan.y);
                clampTranslate();
                render(false);

                if (event.cancelable) {
                    event.preventDefault();
                }
                event.stopPropagation();
            }
        }
    }

    /**
     * @param {TouchEvent} event - the touchend or touchcancel event
     */
    function onTouchEnd(event) {
        if (!enabled) {
            return;
        }

        // A finger was lifted out of a pinch, carry on panning with the one that is left.
        if (event.touches.length === 1 && pinch) {
            pinch = null;

            if (isZoomed()) {
                pan = { x: event.touches[0].clientX, y: event.touches[0].clientY, tx: tx, ty: ty };
            } else {
                pan = null;
                reset(true);
            }
            event.stopPropagation();
            return;
        }

        if (event.touches.length > 0) {
            return;
        }

        var wasPinching = !!pinch;
        var wasPanning = !!pan;

        pinch = null;
        pan = null;

        if (wasPinching) {
            // Pinching back down to the normal size snaps out of the zoom.
            if (!isZoomed()) {
                reset(true);
            }
            event.stopPropagation();
        } else if (!touchMoved && touchStart && event.type === 'touchend') {
            // A tap. Checked before the pan below, because a tap that lands while the image is
            // zoomed in opens a pan that never moved, and that is how a double tap zooms back out.
            handleTap(event);
        } else if (wasPanning) {
            event.stopPropagation();
        }

        touchStart = null;
        suppressCarousel = isZoomed();
    }

    /**
     * @returns {boolean} true while the carousel must not react to the gesture
     */
    function shouldSuppressCarousel() {
        return suppressCarousel || isZoomed() || pointersDown >= 2;
    }

    /**
     * Keeps Bootstrap's pointer driven swipe out of our gestures. Mouse pointers are ignored so
     * nothing changes for a desktop session.
     * @param {PointerEvent} event - a pointer event on the carousel
     */
    function onPointerDown(event) {
        if (event.pointerType === 'mouse') {
            return;
        }

        pointersDown += 1;

        if (shouldSuppressCarousel()) {
            event.stopPropagation();
        }
    }

    /**
     * @param {PointerEvent} event - a pointer event on the carousel
     */
    function onPointerUp(event) {
        if (event.pointerType === 'mouse') {
            return;
        }

        pointersDown = Math.max(0, pointersDown - 1);

        // Runs before touchend, so a swipe that ended a pinch or a pan is still swallowed here.
        if (shouldSuppressCarousel()) {
            event.stopPropagation();
        }

        if (pointersDown === 0) {
            suppressCarousel = isZoomed();
        }
    }

    // Listening in the capture phase means these run before Bootstrap's own handlers on the very
    // same element, so stopPropagation() is enough to keep a slide from being triggered.
    root.addEventListener('touchstart', onTouchStart, { passive: false, capture: true });
    root.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    root.addEventListener('touchend', onTouchEnd, { passive: false, capture: true });
    root.addEventListener('touchcancel', onTouchEnd, { passive: false, capture: true });

    if (window.PointerEvent) {
        root.addEventListener('pointerdown', onPointerDown, true);
        root.addEventListener('pointerup', onPointerUp, true);
        root.addEventListener('pointercancel', onPointerUp, true);
    }

    // Legacy iOS Safari (before touch-action support) zooms the page on its own gesture events.
    root.addEventListener('gesturestart', function (event) {
        if (enabled && event.cancelable) {
            event.preventDefault();
        }
    });

    $(root).on('slide.bs.carousel', function () {
        reset(false);
        img = null;
    });

    return {
        enable: function () {
            enabled = true;
            root.classList.add(ENABLED_CLASS);
            img = activeImage();
            reset(false);
        },
        disable: function () {
            enabled = false;
            reset(false);
            root.classList.remove(ENABLED_CLASS);
            img = null;
        },
        reset: function () {
            reset(false);
        }
    };
}

/**
 * Returns the controller for a container, creating it on first use.
 * @param {Element} root - image container
 * @returns {Object} the controller
 */
function controllerFor(root) {
    var controller = controllers.get(root);

    if (!controller) {
        controller = createController(root);
        controllers.set(root, controller);
    }

    return controller;
}

/**
 * Runs a callback against every product image container on the page.
 * @param {Function} callback - receives each controller
 */
function eachContainer(callback) {
    Array.prototype.forEach.call(document.querySelectorAll(ROOT_SELECTOR), function (root) {
        callback(controllerFor(root));
    });
}

module.exports = {
    /**
     * Turns the touch gestures on for every product image currently in the page.
     */
    init: function () {
        eachContainer(function (controller) {
            controller.enable();
        });
    },

    /**
     * Turns the touch gestures off and clears any zoom left on the images.
     */
    destroy: function () {
        eachContainer(function (controller) {
            controller.disable();
        });
    }
};
